import { Tooltip } from 'antd';
import React, { useState, useEffect, useRef, useCallback } from 'react';

interface AutoHideTooltipProps {
  title: React.ReactNode;
  children: React.ReactElement;
  duration?: number;
  triggerTooltipTimer?: boolean;
  disableTriggerTooltipTimer?: () => void;
  disableHoverInteraction?: boolean;
  [key: string]: unknown;
}

export const AutoHideTooltip = ({
  title,
  children,
  duration = 10_000, // Default duration of 10 seconds
  triggerTooltipTimer = false,
  disableTriggerTooltipTimer,
  disableHoverInteraction = false,
  ...restProps
}: AutoHideTooltipProps) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(() => {
    clearTimer();
    setVisible(true);

    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        disableTriggerTooltipTimer?.();
        setVisible(false);
      }, duration);
    }
  }, [duration, disableTriggerTooltipTimer, clearTimer]);

  const hideTooltip = useCallback(() => {
    clearTimer();
    disableTriggerTooltipTimer?.();
    setVisible(false);
  }, [clearTimer, disableTriggerTooltipTimer]);

  useEffect(() => {
    if (triggerTooltipTimer) {
      showTooltip();
    }
  }, [triggerTooltipTimer, showTooltip]);

  // clears the timer when the component unmounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => clearTimer, []);

  // Se disableHoverInteraction for true, não adiciona eventos de mouse
  const childProps = disableHoverInteraction
    ? {
        onClick: (e: React.MouseEvent) => {
          children.props.onClick?.(e);
          hideTooltip();
        },
      }
    : {
        onMouseEnter: (e: React.MouseEvent) => {
          children.props.onMouseEnter?.(e);
          showTooltip();
        },
        onMouseLeave: (e: React.MouseEvent) => {
          children.props.onMouseLeave?.(e);
          hideTooltip();
        },
        onClick: (e: React.MouseEvent) => {
          children.props.onClick?.(e);
          hideTooltip();
        },
      };

  return (
    <Tooltip
      title={title}
      open={visible}
      onOpenChange={(isVisible) => !isVisible && hideTooltip()}
      // Quando disableHoverInteraction é true, define trigger como 'click' para evitar hover automático
      trigger={disableHoverInteraction ? 'click' : 'hover'}
      {...restProps}
    >
      {React.cloneElement(children, childProps)}
    </Tooltip>
  );
};
